// Channel @-mention QUEUE state — "your message is in line", on the message itself.
//
// When a human @-mentions a cybo that is already mid-run in that channel, the daemon
// HOLDS the message behind the running turn (dispatcher.ts's queueMentionIfVenueBusy)
// and delivers it when that turn ends, instead of the old "@x is busy right now —
// please try again" bounce that measurably lost two messages. This is the honest
// signal back to the human: the daemon broadcasts { messageId, cyboId } when it holds
// and again when it releases (or expires), author-scoped by the relay, and
// ChatMessage renders a "Queued for @x" pill in the same slot Slack uses for
// "sending…".
//
// IN-MEMORY BY DESIGN, and that is a correctness property, not a shortcut. The queue
// itself only exists in the daemon's memory (CyborgDispatcher.mentionQueues — a daemon
// restart drops it), so a persisted mirror here could easily outlive the queue it
// describes and show a "Queued" pill on a message that was answered, or abandoned,
// long ago. A reload showing NO badge is merely incomplete; a reload showing a stale
// badge is a lie. So this store starts empty on every load.
//
// Sibling of slash-progress.svelte.ts (ephemeral, broadcast-driven, safety
// auto-clear) — same shape, same reasons.

// Safety auto-clear, just past the daemon's longest hold (the 60min busy-prompt queue bound; a channel mention's 30min),
// after which it expires a held mention and broadcasts the clear itself. This only
// covers the case where that clear never arrives — the daemon died, or the frame was
// dropped mid-flight — so a live tab can't keep a pill up forever on a wait that
// ended. Same belt-and-braces the slash indicator uses against a lost response.
const QUEUE_STALE_MS = 61 * 60_000;

import type { HoldReason } from "./mention-hold-copy.js";
import type { SignedCardAction } from "$lib/core/types.js";

/** The asker is the one approver: the pill carries their own card's buttons. */
export interface HoldAsk {
  holdId: string;
  actions: SignedCardAction[];
}

interface Held {
  cyboId: string;
  reason?: HoldReason;
  ownerName?: string | null;
  write?: boolean;
  ask?: HoldAsk;
}

class MentionQueueState {
  // messageId -> the hold (cybo it waits on + optional share-hold reason).
  private held = $state<Record<string, Held>>({});
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  // The cybo this message is queued for, or undefined when it isn't queued.
  cyboFor(messageId: string | null | undefined): string | undefined {
    return messageId ? this.held[messageId]?.cyboId : undefined;
  }

  // The share-hold reason + owner for this message, if it is held for one.
  holdFor(messageId: string | null | undefined): Held | undefined {
    return messageId ? this.held[messageId] : undefined;
  }

  hold(
    messageId: string,
    cyboId: string,
    reason?: HoldReason,
    ownerName?: string | null,
    write?: boolean,
    ask?: HoldAsk,
  ): void {
    // fallback: keep the write flag learned on an earlier event for this message
    write = write || this.held[messageId]?.write;
    this.held = { ...this.held, [messageId]: { cyboId, reason, ownerName, write, ...(ask ? { ask } : {}) } }; // prettier-ignore
    this.clearTimer(messageId);
    this.timers.set(
      messageId,
      setTimeout(() => this.release(messageId), QUEUE_STALE_MS),
    );
  }

  // An agent's queue frame: a plain hold on one of its prompts (`c:<promptId>`) it no longer lists
  // has left the queue (ran, refused or removed): no row waits a stale timer in the bar.
  releaseUnlisted(agentId: string, listed: ReadonlySet<string>): void {
    for (const [key, h] of Object.entries(this.held)) {
      if (key.startsWith("c:") && h.cyboId === agentId && !h.reason && !listed.has(key.slice(2)))
        this.release(key);
    }
  }

  release(messageId: string): void {
    if (!(messageId in this.held)) return;
    const next = { ...this.held };
    delete next[messageId];
    this.held = next;
    this.clearTimer(messageId);
  }

  private clearTimer(messageId: string): void {
    const t = this.timers.get(messageId);
    if (t) {
      clearTimeout(t);
      this.timers.delete(messageId);
    }
  }
}

export const mentionQueueState = new MentionQueueState();
